import { SkeletonTextSingle } from "features/CenterContent/RoleContent/ProductionDeviation/components/SkeletonTextSingle";
import { BatchTrainForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import {
  substitudePreviewText,
  useProductionDeviationPath,
} from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import { useTrainInfoBatchPreviews } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoBatchPreviews";
import { FC } from "react";
import { useWatch } from "react-hook-form";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { Text } from "shared/components/Spor";

type SummaryPreviewProps = {
  trainSerie: string;
};

/**
 * Gives a preview in a format like this:
 * [Gjeldende tog] står på Halden. Andre tog er forsinket.
 * [Gjeldende tog] står på Oslo S. Andre tog er forsinket.
 */
export const SummaryPreview: FC<SummaryPreviewProps> = ({ trainSerie }) => {
  const { path } = useProductionDeviationPath(trainSerie);
  const formData = useWatch({ name: path("batchTrainForm") }) as BatchTrainForm;
  const { previewData, previewStatus } = useTrainInfoBatchPreviews({
    formData,
  });
  if (previewStatus === "error") {
    return <FailureMessage flex={1} m="0px" maxW="100%" />;
  }
  if (previewStatus === "pending") return <SkeletonTextSingle />;
  const previewTexts = formData
    .map((formDataDirection) => {
      const representativeTrainId = formDataDirection.trainIdsInGroup[0];
      if (!representativeTrainId) return null;
      const preview = previewData?.find(
        (it) => it.train.identifier === representativeTrainId.identifier,
      );
      const previewText = preview?.preview.internalMessage;
      if (!previewText) return null;
      return substitudePreviewText(
        previewText,
        representativeTrainId.identifier,
      );
    })
    .filter(Boolean);
  if (previewTexts.length === 0) {
    // Should be atleast one previewText per trainSerie
    return <FailureMessage flex={1} m="0px" maxW="100%" />;
  }
  return previewTexts.map((it, i) => (
    // eslint-disable-next-line react/no-array-index-key -- Texts might be the same, so index is the safest key
    <Text whiteSpace="pre-line" key={i}>
      {it}
    </Text>
  ));
};
